import { useEffect, useMemo, useRef, useState } from "react";
import { fieldTime } from "utils/fieldFormat";
import { formatMm as mm } from "utils/radarDiagnostics";
import { pickDistance, spectrumReachMm } from "utils/radarSignal";
import {
  colourRange,
  columnAt,
  columnSpans,
  rampColor,
  rampGradient,
  windowText,
} from "utils/radarStream";
import { LIVE_COLORS, NEAR_RANGE_MM } from "./RadarSpectrum";
import { More } from "./radarUi";

// Radar live: every spectrum of the window on one canvas - time across,
// distance down, colour = strength - with the radar's own pick on top. A
// click (or the arrow keys) picks a column to inspect. Rich wakes draws its
// burst history with it too (one column per burst, windows of days), with
// its own words (`labels`).
export const WATERFALL_HEIGHT = 260;
const NO_LINES = [];   // a stable default: the draw effect depends on it
export const WATERFALL_PAD = { left: 52, right: 10, top: 8, bottom: 24 };
const GAP_FILL = "#eef2f4";
const AXIS = "#6d8490";
const FONT = "10px system-ui, -apple-system, 'Segoe UI', sans-serif";
const TICK_MINUTES = { 15: 5, 60: 15, 180: 30, 720: 120 };
const TICK_LADDER = [5, 15, 30, 60, 120, 180, 360, 720, 1440, 2880, 7200];
const SGT_OFFSET_MS = 8 * 3600000;

// Minutes between time ticks: the stream's windows as before, otherwise the
// first step giving at most eight ticks.
export const tickMinutes = (minutes) =>
  TICK_MINUTES[minutes] ||
  TICK_LADDER.find((step) => minutes / step <= 8) ||
  TICK_LADDER[TICK_LADDER.length - 1];
// Tick times in the window, on whole steps of Singapore time.
export function timeTicks(from, to, minutes) {
  const tick = tickMinutes(minutes) * 60000;
  const ticks = [];
  for (
    let t = Math.ceil((from + SGT_OFFSET_MS) / tick) * tick - SGT_OFFSET_MS;
    t <= to;
    t += tick
  )
    ticks.push(t);
  return ticks;
}

const clock = (ms, seconds = false) =>
  new Intl.DateTimeFormat("en-SG", {
    timeZone: "Asia/Singapore",
    hour: "2-digit",
    minute: "2-digit",
    ...(seconds ? { second: "2-digit" } : {}),
    hour12: false,
  }).format(new Date(ms));
const day = (ms) =>
  new Intl.DateTimeFormat("en-SG", {
    timeZone: "Asia/Singapore",
    day: "numeric",
    month: "short",
  }).format(new Date(ms));
// A column's time: with the date over windows of a day or more.
const columnTime = (ms, minutes) =>
  minutes >= 1440 ? fieldTime(new Date(ms).toISOString()) : clock(ms, true);

const STREAM_LABELS = {
  aria: "Waterfall: choose a capture to inspect",
  empty: (span) =>
    `No captures in the last ${span}. Choose a longer window, or start streaming (it needs live mode).`,
  hint: "Point at the waterfall to read it; click a column to inspect it.",
  pick: "Radar's pick",
  gap: "No capture, or one without a spectrum",
  caption: (span) =>
    `Time across (${span} to now), distance from the radar down, colour from weak (dark) to strong (yellow) between the 20th percentile and the strongest bins of the window, in 0.5 dB steps. A capture without a spectrum (beat signal only, say) leaves a gap.`,
};

// Where everything sits for a canvas `width` px wide: the bins shown (0-2 m
// or all of them), each a band from half a bin above to half below it, and
// the window's time across.
export function waterfallLayout(wf, range, width, from, to) {
  const step = wf.step || 30;
  const cal = wf.cal ?? 0;
  const firstBin = range === "full" ? 0 : Math.max(0, Math.ceil(cal / step));
  const lastBin =
    range === "full"
      ? wf.bins - 1
      : Math.min(wf.bins - 1, Math.floor((NEAR_RANGE_MM + cal) / step));
  const pw = Math.max(1, width - WATERFALL_PAD.left - WATERFALL_PAD.right);
  const ph = WATERFALL_HEIGHT - WATERFALL_PAD.top - WATERFALL_PAD.bottom;
  const top = firstBin * step - cal - step / 2;
  const bottom = lastBin * step - cal + step / 2;
  const span = Math.max(1, to - from);
  return {
    firstBin,
    rows: Math.max(0, lastBin - firstBin + 1),
    pw,
    ph,
    top,
    bottom,
    from,
    to,
    x: (ms) => WATERFALL_PAD.left + ((ms - from) / span) * pw,
    t: (x) => from + ((x - WATERFALL_PAD.left) / pw) * span,
    y: (d) => WATERFALL_PAD.top + ((d - top) / (bottom - top)) * ph,
    d: (y) => top + ((y - WATERFALL_PAD.top) / ph) * (bottom - top),
  };
}

function draw(canvas, width, wf, spans, scale, g, selected, minutes, lines = []) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(WATERFALL_HEIGHT * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, WATERFALL_HEIGHT);
  const { left, top } = WATERFALL_PAD;
  ctx.fillStyle = GAP_FILL;
  ctx.fillRect(left, top, g.pw, g.ph);

  // One pixel per screen column and bin, scaled up without smoothing.
  if (scale && g.rows > 0) {
    const cols = Math.max(1, Math.round(g.pw));
    const lut = Array.from({ length: 256 }, (_, code) =>
      rampColor((code - scale.lo) / (scale.hi - scale.lo)),
    );
    const image = ctx.createImageData(cols, g.rows);
    for (let px = 0; px < cols; px++) {
      const i = columnAt(spans, g.from + ((px + 0.5) / cols) * (g.to - g.from));
      if (i === null || wf.columns[i].empty) continue;
      const codes = wf.columns[i].codes;
      for (let r = 0; r < g.rows; r++) {
        const rgb = lut[codes[g.firstBin + r]];
        const o = (r * cols + px) * 4;
        image.data[o] = rgb[0];
        image.data[o + 1] = rgb[1];
        image.data[o + 2] = rgb[2];
        image.data[o + 3] = 255;
      }
    }
    const tile = document.createElement("canvas");
    tile.width = cols;
    tile.height = g.rows;
    const tileCtx = tile.getContext("2d");
    if (tileCtx) {
      tileCtx.putImageData(image, 0, 0);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(tile, left, top, g.pw, g.ph);
    }
  }

  // The radar's own pick for each column, where it is a valid one.
  ctx.fillStyle = LIVE_COLORS.radar;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
  ctx.lineWidth = 1;
  const reach = spectrumReachMm(wf.step, wf.cal, wf.bins);
  wf.columns.forEach((c, i) => {
    const d = pickDistance(c.air, reach);
    if (d === null || d < g.top || d > g.bottom) return;
    const x = g.x((spans[i].start + spans[i].end) / 2);
    if (x < left || x > left + g.pw) return;
    ctx.beginPath();
    ctx.arc(x, g.y(d), 2.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  });

  // Our estimate for each column where it has one (the burst history: the
  // server's analysis of the burst), a small square so it reads apart from
  // the radar's round pick in print and for colour-blind eyes.
  ctx.fillStyle = LIVE_COLORS.ours;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
  wf.columns.forEach((c, i) => {
    const d = pickDistance(c.ours, reach);
    if (d === null || d < g.top || d > g.bottom) return;
    const x = g.x((spans[i].start + spans[i].end) / 2);
    if (x < left || x > left + g.pw) return;
    const y = g.y(d);
    ctx.beginPath();
    ctx.moveTo(x - 2.2, y - 2.2);
    ctx.lineTo(x + 2.2, y - 2.2);
    ctx.lineTo(x + 2.2, y + 2.2);
    ctx.lineTo(x - 2.2, y + 2.2);
    ctx.lineTo(x - 2.2, y - 2.2);
    ctx.fill();
    ctx.stroke();
  });

  // Fixed distances across the whole window (lines prop), dashed: say, the estimated soil surface.
  for (const line of lines) {
    const d = Number(line?.mm);
    if (!Number.isFinite(d) || d < g.top || d > g.bottom) continue;
    const y = g.y(d);
    ctx.save();
    ctx.strokeStyle = line.color || "#ffffff";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(left + g.pw, y);
    ctx.stroke();
    ctx.restore();
  }

  if (selected !== null && spans[selected]) {
    const x = g.x((spans[selected].start + spans[selected].end) / 2);
    ctx.strokeStyle = LIVE_COLORS.echo;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, top + g.ph);
    ctx.stroke();
    ctx.fillStyle = LIVE_COLORS.echo;
    ctx.beginPath();
    ctx.moveTo(x - 5, top);
    ctx.lineTo(x + 5, top);
    ctx.lineTo(x, top + 6);
    ctx.closePath();
    ctx.fill();
  }

  ctx.fillStyle = AXIS;
  ctx.font = FONT;
  ctx.textAlign = "right";
  const every = g.bottom - g.top > 3000 ? 1000 : 500;
  for (let d = Math.ceil(g.top / every) * every; d <= g.bottom; d += every)
    ctx.fillText(
      d === 0 ? "0" : `${(d / 1000).toFixed(1)} m`,
      left - 6,
      g.y(d) + 3,
    );
  ctx.textAlign = "center";
  const days = tickMinutes(minutes) >= 1440;
  for (const t of timeTicks(g.from, g.to, minutes)) {
    const x = g.x(t);
    if (x >= left + 14 && x <= left + g.pw - 14)
      ctx.fillText(days ? day(t) : clock(t), x, WATERFALL_HEIGHT - 8);
  }
}

export default function RadarWaterfall({
  data,
  minutes,
  range = "near",
  now,
  selectedKey = null,
  onSelect,
  labels: words = null,
  lines = NO_LINES,
}) {
  const labels = { ...STREAM_LABELS, ...words };
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const layout = useRef(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState(null);
  const columns = useMemo(() => data?.columns || [], [data]);
  const spans = useMemo(() => columnSpans(columns), [columns]);
  const scale = useMemo(() => colourRange(columns), [columns]);
  const selected = useMemo(() => {
    const i =
      selectedKey === null ? -1 : columns.findIndex((c) => c.t === selectedKey);
    return i >= 0 ? i : null;
  }, [columns, selectedKey]);
  const lastEnd = spans.length ? spans[spans.length - 1].end : -Infinity;
  const to = Math.max(now, lastEnd);
  const from = to - minutes * 60000;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect?.width || 0);
      if (w) setWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;
    const w = width || Math.round(canvas.getBoundingClientRect().width) || 720;
    const g = waterfallLayout(data, range, w, from, to);
    layout.current = g;
    draw(canvas, w, data, spans, scale, g, selected, minutes, lines);
  }, [data, range, width, from, to, spans, scale, selected, minutes, lines]);

  if (!data) return null;
  if (!columns.length)
    return (
      <div className="field-radar-empty">
        {labels.empty(windowText(minutes))}
      </div>
    );

  const at = (event) => {
    const g = layout.current;
    const canvas = canvasRef.current;
    if (!g || !canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width
      ? (g.pw + WATERFALL_PAD.left + WATERFALL_PAD.right) / rect.width
      : 1;
    const x = (event.clientX - rect.left) * scaleX;
    const y = event.clientY - rect.top;
    if (x < WATERFALL_PAD.left || x > WATERFALL_PAD.left + g.pw) return null;
    const index = columnAt(spans, g.t(x));
    return { index, distance: g.d(y), y, g };
  };
  const choose = (index) => {
    if (index === null || index >= columns.length) onSelect?.(null);
    else onSelect?.(columns[index], index);
  };
  const onKeyDown = (event) => {
    const current = selected ?? columns.length - 1;
    const moves = {
      ArrowLeft: current - 1,
      ArrowRight: selected === null ? null : current + 1,
      PageUp: current - 10,
      PageDown: selected === null ? null : current + 10,
      Home: 0,
      End: null,
      Escape: null,
    };
    if (!(event.key in moves)) return;
    event.preventDefault();
    const next = moves[event.key];
    choose(next === null ? null : Math.max(0, next));
  };
  const onMove = (event) => {
    const hit = at(event);
    if (
      !hit ||
      hit.index === null ||
      hit.y < WATERFALL_PAD.top ||
      hit.y > WATERFALL_PAD.top + hit.g.ph
    ) {
      setHover(null);
      return;
    }
    const c = columns[hit.index];
    const bin = Math.round(
      (hit.distance + (data.cal ?? 0)) / (data.step || 30),
    );
    const code = c.empty ? null : c.codes[bin];
    setHover({
      t: c.ms,
      distance: bin * (data.step || 30) - (data.cal ?? 0),
      db:
        code === null || code === undefined
          ? null
          : data.dbMin + code * data.dbStep,
    });
  };
  const shownIndex = selected ?? columns.length - 1;
  const shownColumn = columns[shownIndex];
  const shownPick = pickDistance(
    shownColumn?.air,
    spectrumReachMm(data.step, data.cal, data.bins),
  );

  return (
    <div className="field-stream-waterfall" ref={wrapRef}>
      <canvas
        ref={canvasRef}
        role="slider"
        tabIndex={0}
        aria-label={labels.aria}
        aria-valuemin={0}
        aria-valuemax={columns.length - 1}
        aria-valuenow={shownIndex}
        aria-valuetext={`${columnTime(shownColumn.ms, minutes)} SGT${selected === null ? " (latest)" : ""}${shownPick !== null ? ` · radar ${mm(shownPick)}` : ""}`}
        style={{ height: WATERFALL_HEIGHT }}
        onClick={(event) => {
          const hit = at(event);
          if (hit && hit.index !== null) choose(hit.index);
        }}
        onKeyDown={onKeyDown}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      />
      <div className="field-stream-scale">
        <span>
          {scale
            ? `${(data.dbMin + scale.lo * data.dbStep).toFixed(0)} dB`
            : "—"}
        </span>
        <span
          className="field-stream-ramp"
          style={{ background: rampGradient() }}
          aria-hidden="true"
        />
        <span>
          {scale
            ? `${(data.dbMin + scale.hi * data.dbStep).toFixed(0)} dB`
            : "—"}
        </span>
        <span className="field-stream-hover" aria-live="off">
          {hover
            ? `${columnTime(hover.t, minutes)} SGT · ${mm(hover.distance)} · ${hover.db === null ? "no spectrum" : `${hover.db.toFixed(1)} dB`}`
            : labels.hint}
        </span>
      </div>
      <ul className="field-radar-legend" aria-label="Waterfall key">
        <li>
          <span
            className="field-radar-key is-dot"
            style={{ "--key": LIVE_COLORS.radar }}
            aria-hidden="true"
          />
          {labels.pick}
        </li>
        {(data?.columns || []).some((c) => c.ours !== null && c.ours !== undefined) && (
          <li>
            <span
              className="field-radar-key is-square"
              style={{ "--key": LIVE_COLORS.ours }}
              aria-hidden="true"
            />
            {labels.ours || "Our estimate"}
          </li>
        )}
        {lines.map((line) => (
          <li key={line.label}>
            <span className="field-radar-key is-dashed" style={{ "--key": line.color || "#ffffff" }} aria-hidden="true" />
            {line.label}
          </li>
        ))}
        <li>
          <span className="field-radar-key is-tick" aria-hidden="true" />
          Column being inspected
        </li>
        <li>
          <span
            className="field-radar-key is-area"
            style={{ "--key": GAP_FILL }}
            aria-hidden="true"
          />
          {labels.gap}
        </li>
      </ul>
      {data.decimated && (
        <p className="field-caption">
          {`The window holds more captures than ${columns.length.toLocaleString()} columns, so the server thinned them; a column opens the nearest stored capture.`}
        </p>
      )}
      <More label="How to read it">
        <p className="field-caption">
          {labels.caption(windowText(minutes))} Arrow keys move between columns;
          Esc returns to the latest.
        </p>
      </More>
    </div>
  );
}
